import { useMemo } from "react";
import { singaporeTaxCalculation } from "@/lib/helpers/number";
import { calculateServiceDiscount } from "@/lib/booking-lib";
import { getLineItemClientDetail } from "@/lib/line-items/helpers";
import type { PackageDetailFragmentFragment } from "@/__generated__/graphql";
import { PackageServiceTypeEnum } from "@/__generated__/graphql";
import { useBookingState } from "@/store/booking/useBookingState";
import { formatDecimalHour } from "@/lib/utils";
import { getDeepCleaningPackageFromCode } from "@/constants/booking";

export const useBookingOrder = () => {
  const {
    data: {
      department,
      promo,
      packageDetailsFilter,
      selectedSlot,
      selectedPackageIds,
      packageDetails,
    },
  } = useBookingState();

  return useMemo(() => {
    const duration = packageDetailsFilter.duration;
    const unitPrice = selectedSlot?.rateValue || 0;
    const packages =
      selectedPackageIds
        .map(
          (id) =>
            packageDetails.find(
              (pkg) => pkg.id === id,
            ) as PackageDetailFragmentFragment,
        )
        .filter((val) => !!val) || [];

    const lineItems = packages.map((pkg) =>
      getLineItemClientDetail(pkg, {
        description: pkg.description,
      }),
    );

    const serviceType = lineItems[0]?.serviceType;
    const deepCleaningService =
      serviceType === PackageServiceTypeEnum.DeepCleaning;
    const petSittingService =
      serviceType === PackageServiceTypeEnum.PetSitting ||
      serviceType === PackageServiceTypeEnum.DogWalking;

    const discounts = lineItems.filter(
      (item) => item.serviceType === PackageServiceTypeEnum.Discount,
    );

    let firstSessionValue = 0;
    let totalUnits = 0;
    let totalDuration = 0;
    packages
      .filter((val) => !!val)
      .forEach((pkg) => {
        firstSessionValue +=
          pkg.serviceBillingValue *
          (pkg.repeatEveryTimes > 0 ? pkg.repeatEveryTimes : 1);
        totalUnits += pkg.units;
        totalDuration += pkg.duration;
      });
    const packagesUnitType = packageDetails.map((pkg) => pkg.unitType);
    const { percent: percentDiscount, total: totalDiscount } =
      calculateServiceDiscount(
        totalUnits,
        firstSessionValue,
        department,
        packagesUnitType,
        serviceType,
      );

    if (serviceType === PackageServiceTypeEnum.DeepCleaning) {
      const totalCleaner = getDeepCleaningPackageFromCode(
        packages[0]?.code,
      ).numberOfCleaner;
      firstSessionValue = firstSessionValue * totalCleaner;
    }

    const totalAfterDiscount = firstSessionValue - totalDiscount;
    const { tax, price: grandTotal } = singaporeTaxCalculation(
      totalAfterDiscount - (promo?.computedDiscount || 0),
    );

    const serviceDuration =
      deepCleaningService || petSittingService
        ? `${formatDecimalHour(totalDuration)}`
        : `${formatDecimalHour(totalDuration)} (${totalUnits} Items)`;
    const serviceTimeDuration = formatDecimalHour(totalDuration, false);

    return {
      lineItems,
      duration,
      unitPrice,
      totalBeforeDiscount: firstSessionValue,
      totalAfterDiscount,
      tax,
      discounts,
      percentDiscount,
      totalDiscount,
      grandTotal,
      promo,
      serviceDuration,
      serviceTimeDuration,
    };
  }, [
    selectedSlot,
    packageDetails,
    promo,
    selectedPackageIds,
    selectedPackageIds.length,
  ]);
};
